import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Bell, Check, Loader2, Trash2, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

function fmtTs(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/** End-user notification inbox — shows every push that targeted this user. */
export default function NotificationsInboxPage() {
  const [items, setItems] = useState(null);
  const [marking, setMarking] = useState(false);
  const [completingId, setCompletingId] = useState(null);

  const load = useCallback(() => {
    api
      .get("/notifications/inbox", { params: { limit: 100 } })
      .then(({ data }) => setItems(data || []))
      .catch(() => setItems([]));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onMarkOne = async (delivery_id) => {
    try {
      await api.post(`/notifications/${delivery_id}/read`);
      setItems((prev) =>
        prev.map((d) =>
          d.delivery_id === delivery_id
            ? { ...d, read_at: new Date().toISOString() }
            : d
        )
      );
    } catch (err) {
      toast.error(formatApiError(err, "Could not mark as read"));
    }
  };

  const onMarkAll = async () => {
    setMarking(true);
    try {
      await api.post("/notifications/read-all");
      const now = new Date().toISOString();
      setItems((prev) =>
        prev.map((d) => (d.read_at ? d : { ...d, read_at: now }))
      );
      toast.success("All caught up");
    } catch (err) {
      toast.error(formatApiError(err, "Could not mark all as read"));
    } finally {
      setMarking(false);
    }
  };

  const onDeleteOne = async (delivery_id) => {
    // Optimistic — restore on failure.
    const prev = items;
    setItems((cur) => cur.filter((d) => d.delivery_id !== delivery_id));
    try {
      await api.delete(`/notifications/inbox/${delivery_id}`);
    } catch (err) {
      setItems(prev);
      toast.error(formatApiError(err, "Could not delete notification"));
    }
  };

  const onClearRead = async () => {
    setMarking(true);
    try {
      const { data } = await api.delete("/notifications/inbox", {
        params: { scope: "read" },
      });
      setItems((prev) => prev.filter((d) => !d.read_at));
      toast.success(
        `Cleared ${data.deleted} read notification${
          data.deleted === 1 ? "" : "s"
        }`
      );
    } catch (err) {
      toast.error(formatApiError(err, "Could not clear notifications"));
    } finally {
      setMarking(false);
    }
  };

  const onMarkQuoteComplete = async (d) => {
    const quoteId = d?.metadata?.quote_request_id;
    if (!quoteId) return;
    setCompletingId(d.delivery_id);
    try {
      await api.post(`/quote-requests/${quoteId}/complete`);
      setItems((prev) =>
        prev.map((it) =>
          it.delivery_id === d.delivery_id
            ? {
                ...it,
                quote_request_status: "completed",
                quote_completed_at: new Date().toISOString(),
                read_at: it.read_at || new Date().toISOString(),
              }
            : it
        )
      );
      toast.success("Marked complete — distributor was notified");
    } catch (err) {
      toast.error(formatApiError(err, "Could not mark complete"));
    } finally {
      setCompletingId(null);
    }
  };

  const unread = (items || []).filter((d) => !d.read_at).length;
  const readCount = (items || []).filter((d) => d.read_at).length;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="notifications-inbox">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center gap-1.5">
            <Bell className="w-3 h-3" />
            Notifications
          </div>
          <h1 className="mt-1 text-3xl font-display font-bold tracking-tight text-slate-900">
            Inbox
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {unread > 0
              ? `${unread} unread notification${unread === 1 ? "" : "s"}`
              : "You're all caught up."}
          </p>
        </div>
        {(unread > 0 || readCount > 0) && (
          <div className="flex items-center gap-2">
            {unread > 0 && (
              <Button
                variant="outline"
                onClick={onMarkAll}
                disabled={marking}
                className="rounded-sm"
                data-testid="mark-all-read-btn"
              >
                {marking && <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />}
                Mark all as read
              </Button>
            )}
            {readCount > 0 && (
              <Button
                variant="outline"
                onClick={onClearRead}
                disabled={marking}
                className="rounded-sm text-slate-600"
                data-testid="clear-read-btn"
              >
                <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                Clear read
              </Button>
            )}
          </div>
        )}
      </div>

      {items === null ? (
        <div className="byrd-progress" />
      ) : items.length === 0 ? (
        <div
          className="border border-dashed border-border rounded-sm bg-white p-12 text-center"
          data-testid="inbox-empty"
        >
          <Bell className="w-8 h-8 mx-auto text-slate-400" />
          <h3 className="mt-3 font-display text-lg font-semibold text-slate-900">
            No notifications yet
          </h3>
          <p className="mt-1 text-sm text-muted-foreground">
            When your rep agency sends an announcement, it&apos;ll show up here.
          </p>
        </div>
      ) : (
        <ul className="space-y-2">
          {items.map((d) => {
            const unread = !d.read_at;
            return (
              <li
                key={d.delivery_id}
                className={`border rounded-sm p-4 transition-colors ${
                  unread
                    ? "bg-amber-50/50 border-amber-200"
                    : "bg-white border-border"
                }`}
                data-testid={`inbox-item-${d.delivery_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-display font-semibold text-slate-900">
                        {d.title}
                      </div>
                      {unread && (
                        <span className="text-[9px] uppercase tracking-widest font-mono-data text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-sm">
                          New
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-700 whitespace-pre-wrap">
                      {d.body}
                    </p>
                    <div className="mt-2 text-[10px] uppercase tracking-wider text-muted-foreground font-mono-data">
                      {fmtTs(d.sent_at)}
                    </div>

                    {d.metadata?.type === "quote_request" && (
                      <div className="mt-3 pt-3 border-t border-amber-200/60">
                        {d.quote_request_status === "completed" ? (
                          <div
                            className="inline-flex items-center gap-1.5 text-xs text-emerald-700 font-medium"
                            data-testid={`quote-completed-${d.delivery_id}`}
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            Completed
                            {d.quote_completed_by_name && (
                              <span className="text-muted-foreground font-normal">
                                by {d.quote_completed_by_name}
                              </span>
                            )}
                            <span className="text-muted-foreground font-normal">
                              · {fmtTs(d.quote_completed_at)}
                            </span>
                          </div>
                        ) : (
                          <label
                            className="inline-flex items-center gap-2 text-sm text-slate-800 cursor-pointer select-none group"
                            data-testid={`mark-complete-label-${d.delivery_id}`}
                          >
                            <input
                              type="checkbox"
                              className="w-4 h-4 accent-slate-900 cursor-pointer"
                              checked={false}
                              disabled={completingId === d.delivery_id}
                              onChange={() => onMarkQuoteComplete(d)}
                              data-testid={`mark-complete-${d.delivery_id}`}
                            />
                            <span className="group-hover:text-slate-900">
                              {completingId === d.delivery_id
                                ? "Marking complete…"
                                : "Mark complete & notify distributor"}
                            </span>
                          </label>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    {unread && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onMarkOne(d.delivery_id)}
                        className="rounded-sm text-xs"
                        data-testid={`mark-read-${d.delivery_id}`}
                      >
                        <Check className="w-3.5 h-3.5 mr-1" />
                        Mark read
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => onDeleteOne(d.delivery_id)}
                      className="rounded-sm text-xs text-slate-500 hover:text-red-700"
                      aria-label="Delete notification"
                      data-testid={`delete-${d.delivery_id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
